import type { ShapePoint, ShapeVertex } from '@repo/api-contracts';
import { type CubicSegment, segmentOf, segmentPointAt } from './cubic-segment';

/** 고리 위에서 가장 가까운 점 — 구간 번호(그 구간 시작 점의 index), 매개변수 t, 점, 거리(mm). */
export interface RingHit {
  readonly segmentIndex: number;
  readonly t: number;
  readonly point: ShapePoint;
  readonly distanceMm: number;
}

const distance = (a: ShapePoint, b: ShapePoint): number => Math.hypot(a.xMm - b.xMm, a.yMm - b.yMm);

/** 곡선 구간을 처음 고를 때 보는 표본 수, 그 뒤 좁혀 가는 횟수. */
const CURVE_SAMPLES = 64;
const REFINE_STEPS = 40;

const nearestOnStraight = (segment: CubicSegment, target: ShapePoint): number => {
  const dx = segment.p3.xMm - segment.p0.xMm;
  const dy = segment.p3.yMm - segment.p0.yMm;
  const lengthSq = dx * dx + dy * dy;
  if (lengthSq === 0) return 0;
  const t = ((target.xMm - segment.p0.xMm) * dx + (target.yMm - segment.p0.yMm) * dy) / lengthSq;
  return Math.min(1, Math.max(0, t));
};

/** 곡선 위 가장 가까운 t — 고른 표본에서 시작해 이웃 간격을 반씩 줄이며 좁힌다. */
const nearestOnCurve = (segment: CubicSegment, target: ShapePoint): number => {
  let best = 0;
  let bestDistance = Number.POSITIVE_INFINITY;
  for (let step = 0; step <= CURVE_SAMPLES; step += 1) {
    const t = step / CURVE_SAMPLES;
    const d = distance(segmentPointAt(segment, t), target);
    if (d < bestDistance) {
      best = t;
      bestDistance = d;
    }
  }
  let span = 1 / CURVE_SAMPLES;
  for (let step = 0; step < REFINE_STEPS; step += 1) {
    span /= 2;
    for (const t of [best - span, best + span]) {
      if (t < 0 || t > 1) continue;
      const d = distance(segmentPointAt(segment, t), target);
      if (d < bestDistance) {
        best = t;
        bestDistance = d;
      }
    }
  }
  return best;
};

/** 바깥 고리(곡선 포함)에서 target 에 가장 가까운 점. 점이 없으면 null. */
export const nearestPointOnRing = (
  points: readonly ShapeVertex[],
  target: ShapePoint,
): RingHit | null => {
  let hit: RingHit | null = null;
  points.forEach((_, index) => {
    const segment = segmentOf(points, index);
    if (segment === null) return;
    const t = segment.curved ? nearestOnCurve(segment, target) : nearestOnStraight(segment, target);
    const point = segmentPointAt(segment, t);
    const distanceMm = distance(point, target);
    if (hit === null || distanceMm < hit.distanceMm)
      hit = { segmentIndex: index, t, point, distanceMm };
  });
  return hit;
};
